import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBackendHealthStore } from "@/lib/backendHealth";
import BackendUnavailablePage from "@/components/BackendUnavailablePage";
import { Outlet, useLocation } from "react-router";
import { Component, useEffect, type ReactNode } from "react";
import { useResolvedTheme } from "@/stores/appearanceStore";
import { cn } from "@/utils/helpers";
import { KitButtonSpinner, kitButtonClass } from "@/app/routes/dashboard/components/kit/Button";
import "@/app/routes/dashboard/cockpit.css";

/** The crash screen: the shell's ground and inks in the reader's theme, one pill. */
function CrashScreen({ error }: { error: Error }) {
  const theme = useResolvedTheme();
  return (
    <div className={cn("co-shell co-page items-center justify-center bg-co-ground p-6", theme === "dark" && "dark")}>
      <div className="flex max-w-[920px] flex-col items-start gap-3.5">
        <p className="text-[15px] font-bold text-co-text">Something broke while rendering this page.</p>
        {import.meta.env.DEV ? (
          <pre className="m-0 whitespace-pre-wrap font-mono text-[12px] leading-[1.5] [overflow-wrap:anywhere] text-co-danger">
            {error.stack ?? String(error)}
          </pre>
        ) : null}
        <button type="button" onClick={() => window.location.reload()} className={kitButtonClass({ variant: "primary", size: "lg" })}>
          Reload
        </button>
      </div>
    </div>
  );
}

/**
 * Last-resort render guard: an uncaught render error below the router root
 * unmounts the whole tree onto html.dark = an unreadable black screen. Dev
 * prints the stack on-screen (self-diagnosing); prod offers a reload.
 */
class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <CrashScreen error={error} />;
  }
}

const RootLayout = () => {
  const { isLoading } = useCurrentUser();
  const { pathname } = useLocation();
  const backendUnavailable = useBackendHealthStore((st) => st.unavailable);
  const retrying = useBackendHealthStore((st) => st.consecutiveFailures > 0);
  const theme = useResolvedTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // The backend stopped answering and every retry is spent: replace the whole
  // tree (which also unmounts every polling query) with a clear stop. Checked
  // before the bootstrap spinner so a dead API during login never strands the
  // user on the blank dark screen.
  if (backendUnavailable) {
    return <BackendUnavailablePage />;
  }

  // Bootstrap wait. Silent while the first attempt is in flight; once a call
  // has failed the screen says so, so the retry window never reads as a hang.
  if (isLoading) {
    return (
      <div className={cn("co-shell co-page items-center justify-center bg-co-ground", theme === "dark" && "dark")}>
        {retrying ? (
          <p role="status" aria-live="polite" className="m-0 flex items-center gap-2.5 text-[13px] font-medium leading-[18px] text-co-muted">
            <KitButtonSpinner />
            Having trouble reaching Mohtawa, retrying…
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <RouteErrorBoundary>
      <Outlet />
    </RouteErrorBoundary>
  );
};

export default RootLayout;
